import { useAtomValue } from 'jotai'

import { useConfig, useWalletClient } from 'wagmi'

import { OrderClass } from '@cowprotocol/cow-sdk'
import type { Token } from '@cowprotocol/currency'
import { isSafeWalletAtom, useWalletDetails, useWalletInfo } from '@cowprotocol/wallet'

import { useAddBridgeOrder } from 'entities/bridgeOrders'
import { useDispatch } from 'react-redux'
import useSWR from 'swr'

import { AppDispatch } from 'legacy/state'
import { useCloseModals } from 'legacy/state/application/hooks'

import { useAppData, useAppDataHooks, useWaitAndSaveValidFrom } from 'modules/appData'
import { useBridgeQuoteAmounts } from 'modules/bridge'
import { useGetAmountToSignApprove } from 'modules/erc20Approve'
import { useGeneratePermitHook, useGetCachedPermit, usePermitInfo } from 'modules/permit'
import {
  TradeTypeToUiOrderType,
  useDerivedTradeState,
  useGetReceiveAmountInfo,
  useIsHooksTradeType,
  useTradeConfirmActions,
  useTradeTypeInfo,
} from 'modules/trade'
import { getIsFinalQuote, getOrderValidTo, useTradeQuote } from 'modules/tradeQuote'

import { useGP2SettlementContractData } from 'common/hooks/useContract'
import { useEnoughAllowance } from 'common/hooks/useEnoughAllowance'

import { useSetSigningStep } from './useSetSigningStep'

import { TradeFlowContext } from '../types/TradeFlowContext'

export interface TradeFlowParams {
  deadline: number
}

// TODO: Break down this large function into smaller functions
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line max-lines-per-function, complexity
export function useTradeFlowContext({ deadline }: TradeFlowParams): TradeFlowContext | null {
  const config = useConfig()
  const { data: walletClient } = useWalletClient()
  const { account } = useWalletInfo()
  const { allowsOffchainSigning } = useWalletDetails()
  const isSafeWallet = useAtomValue(isSafeWalletAtom)
  const derivedTradeState = useDerivedTradeState()
  const receiveAmountInfo = useGetReceiveAmountInfo()
  const tradeTypeInfo = useTradeTypeInfo()
  const tradeType = tradeTypeInfo?.tradeType
  const uiOrderType = tradeType ? TradeTypeToUiOrderType[tradeType] : null
  const isHooksTradeType = useIsHooksTradeType()
  const setSigningStep = useSetSigningStep()

  const tradeQuote = useTradeQuote()
  const bridgeContext = useBridgeQuoteAmounts()

  const sellCurrency = derivedTradeState?.inputCurrency
  const { sellAmount: inputAmount, buyAmount: outputAmount } = receiveAmountInfo?.amountsToSign ?? {}
  const bridgeOutputAmount = bridgeContext?.bridgeMinReceiveAmount

  const sellAmountBeforeFee = receiveAmountInfo?.afterNetworkCosts.sellAmount
  const networkFee = receiveAmountInfo?.costs.networkFee.amountInSellCurrency

  const permitInfo = usePermitInfo(sellCurrency, tradeType)
  const generatePermitHook = useGeneratePermitHook()
  const getCachedPermit = useGetCachedPermit()
  const closeModals = useCloseModals()
  const dispatch = useDispatch<AppDispatch>()
  const tradeConfirmActions = useTradeConfirmActions()
  const settlementContract = useGP2SettlementContractData()
  const appData = useAppData()
  const typedHooks = useAppDataHooks()
  const addBridgeOrder = useAddBridgeOrder()
  const bridgeQuoteAmounts = useBridgeQuoteAmounts()
  const amountToSignApprove = useGetAmountToSignApprove()
  const permitAmountToSign = amountToSignApprove ? BigInt(amountToSignApprove.quotient.toString()) : undefined

  const enoughAllowance = useEnoughAllowance(inputAmount)

  const {
    inputCurrency: sellToken,
    outputCurrency: buyToken,
    recipient,
    recipientAddress,
    orderKind,
  } = derivedTradeState || {}

  const waitAndSaveValidFrom = useWaitAndSaveValidFrom()
  const validTo = getOrderValidTo(deadline, tradeQuote, waitAndSaveValidFrom)

  const settlementChainId = settlementContract.chainId

  return (
    useSWR(
      inputAmount &&
        outputAmount &&
        sellAmountBeforeFee &&
        networkFee &&
        sellToken &&
        buyToken &&
        account &&
        appData &&
        tradeQuote.quote &&
        getIsFinalQuote(tradeQuote.fetchParams) &&
        orderKind &&
        settlementContract &&
        uiOrderType &&
        validTo > 0 &&
        walletClient
        ? [
            account,
            allowsOffchainSigning,
            appData,
            tradeQuote,
            tradeQuote.quote,
            buyToken,
            settlementChainId,
            closeModals,
            dispatch,
            enoughAllowance,
            generatePermitHook,
            permitAmountToSign,
            inputAmount,
            networkFee,
            outputAmount,
            permitInfo,
            recipient,
            recipientAddress,
            sellAmountBeforeFee,
            sellToken,
            settlementContract,
            tradeConfirmActions,
            typedHooks,
            validTo,
            orderKind,
            uiOrderType,
            bridgeQuoteAmounts,
            addBridgeOrder,
            setSigningStep,
            walletClient,
            config,
          ]
        : null,
      // TODO: Break down this large function into smaller functions
      // eslint-disable-next-line max-lines-per-function
      ([
        account,
        allowsOffchainSigning,
        appData,
        tradeQuoteState,
        tradeQuote,
        buyToken,
        chainId,
        closeModals,
        dispatch,
        enoughAllowance,
        generatePermitHook,
        permitAmountToSign,
        inputAmount,
        networkFee,
        outputAmount,
        permitInfo,
        recipient,
        recipientAddress,
        sellAmountBeforeFee,
        sellToken,
        settlementContract,
        tradeConfirmActions,
        typedHooks,
        validTo,
        orderKind,
        uiOrderType,
        bridgeQuoteAmounts,
        addBridgeOrder,
        setSigningStep,
        walletClient,
        config,
      ]) => {
        void settlementContract // in deps for memo stability
        return {
          tradeQuoteState,
          tradeQuote,
          bridgeQuoteAmounts,
          context: {
            chainId,
            inputAmount,
            outputAmount,
            inputAmountWithSlippage: inputAmount,
          },
          flags: {
            allowsOffchainSigning,
          },
          callbacks: {
            closeModals,
            getCachedPermit,
            dispatch,
            addBridgeOrder,
            setSigningStep,
          },
          tradeConfirmActions,
          tradeFlowAnalyticsContext: {
            account,
            recipient,
            recipientAddress,
            marketLabel: [inputAmount?.currency.symbol, outputAmount?.currency.symbol].join(','),
            orderType: uiOrderType,
            isBridgeOrder: inputAmount.currency.chainId !== outputAmount.currency.chainId,
          },
          permitInfo: !enoughAllowance ? permitInfo : undefined,
          generatePermitHook,
          permitAmountToSign,
          typedHooks,
          orderParams: {
            account,
            chainId,
            signer: walletClient,
            kind: orderKind,
            inputAmount,
            outputAmount,
            bridgeOutputAmount,
            sellAmountBeforeFee,
            feeAmount: networkFee,
            sellToken: sellToken as Token,
            buyToken: buyToken as Token,
            validTo,
            recipient: recipientAddress || recipient || account,
            recipientAddressOrName: recipient || null,
            allowsOffchainSigning,
            appData,
            class: OrderClass.MARKET,
            partiallyFillable: isHooksTradeType,
            quoteId: tradeQuote.quoteResults.quoteResponse.id,
            isSafeWallet,
          },
          config,
        }
      },
    ).data || null
  )
}
